import { useCallback, useMemo, useState } from "react";
import { Flex, Text, Badge } from "@radix-ui/themes";
import { Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { RowActions, SimpleTable } from "@/v2/components/table/SimpleTable";
import {
  ArticleResource,
  ReleaseAccessStatus,
  ReleaseAccessResourceTopic,
  getArticleSource,
  articleSourceLabel,
  articleSourceColor,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";
import {
  useGetWikiByRelease,
  useUpdateArticleAccess,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.api";
import { useGetTopics } from "@/api/useGetTopics";
import { AccessBadge } from "../components/AccessBadge";
import { ManageAccessDialog, Topic } from "../components/ManageAccessDialog";
import {
  ResourcesToolbar,
  ResourcesFilterState,
} from "../components/ResourcesToolbar";
import { File02Icon } from "@/v2/assets/icons/File02Icon";

interface WikiTabProps {
  releaseId: number | undefined;
  publishedReleaseId?: number;
  simulatorId?: string;
}

type WikiRow = ArticleResource & {
  isSelected: boolean;
  isStaged: boolean;
  publishedTopicsAccess: ReleaseAccessResourceTopic[];
};

export function WikiTab({ releaseId, publishedReleaseId, simulatorId }: WikiTabProps) {
  const { data: wikiArticles = [], isLoading } = useGetWikiByRelease(releaseId);
  const { data: publishedWikiArticles = [] } = useGetWikiByRelease(publishedReleaseId);
  const { data: topicsData } = useGetTopics();
  const updateArticleAccess = useUpdateArticleAccess(releaseId, simulatorId);

  const topics: Topic[] = useMemo(() => {
    if (!topicsData) return [];
    return topicsData.map((t: { id: number; topic: string }) => ({
      id: t.id,
      topic: t.topic,
    }));
  }, [topicsData]);

  const categories = useMemo(() => {
    const uniqueCategories = new Set(
      wikiArticles
        .map((article) => article.category)
        .filter((category): category is string => Boolean(category?.trim()))
    );

    return ["No category", ...Array.from(uniqueCategories).sort((a, b) => a.localeCompare(b))];
  }, [wikiArticles]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterState, setFilterState] = useState<ResourcesFilterState>({
    accessLevels: [],
    searchQuery: "",
    categories: [],
  });
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    resourceIds: string[];
    resourceName: string;
    currentStatus?: ReleaseAccessStatus;
    currentTopicsAccess?: ReleaseAccessResourceTopic[];
    publishedTopicsAccess?: ReleaseAccessResourceTopic[];
    currentDefaultTopicAccess?: ReleaseAccessStatus | null;
  }>({
    open: false,
    resourceIds: [],
    resourceName: "",
  });

  const publishedStatusMap = useMemo(() => {
    const map = new Map<string, ReleaseAccessStatus>();
    for (const article of publishedWikiArticles) {
      map.set(article.id, article.accessStatus);
    }
    return map;
  }, [publishedWikiArticles]);

  const publishedTopicsAccessMap = useMemo(() => {
    const map = new Map<string, ReleaseAccessResourceTopic[]>();
    for (const article of publishedWikiArticles) {
      map.set(article.id, article.topicsAccess);
    }
    return map;
  }, [publishedWikiArticles]);

  const filteredWikiArticles = useMemo(() => {
    return wikiArticles.filter((article) => {
      const matchesSearch =
        filterState.searchQuery === "" ||
        article.title.toLowerCase().includes(filterState.searchQuery.toLowerCase());

      const matchesAccess =
        filterState.accessLevels.length === 0 ||
        filterState.accessLevels.includes(article.accessStatus);

      const matchesCategory =
        filterState.categories.length === 0 ||
        filterState.categories.includes(article.category || "No category");

      return matchesSearch && matchesAccess && matchesCategory;
    });
  }, [wikiArticles, filterState]);

  const rows: WikiRow[] = useMemo(() => {
    return filteredWikiArticles.map((article) => {
      const publishedStatus = publishedStatusMap.get(article.id);
      const isStaged =
        publishedReleaseId !== undefined &&
        publishedStatus !== undefined &&
        publishedStatus !== article.accessStatus;

      return {
        ...article,
        isSelected: selectedIds.has(article.id),
        isStaged,
        publishedTopicsAccess: publishedTopicsAccessMap.get(article.id) ?? [],
      };
    }).sort((a, b) => a.title?.localeCompare(b.title ?? "") ?? 0);
  }, [filteredWikiArticles, selectedIds, publishedStatusMap, publishedTopicsAccessMap, publishedReleaseId]);

  const allSelected = useMemo(() => {
    return rows.length > 0 && rows.every((row) => row.isSelected);
  }, [rows]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  }, [allSelected, rows]);

  const handleSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleOpenManageAccess = useCallback(
    (
      resourceIds: string[],
      resourceName: string,
      currentStatus?: ReleaseAccessStatus,
      currentTopicsAccess?: ReleaseAccessResourceTopic[],
      publishedTopicsAccess?: ReleaseAccessResourceTopic[],
      currentDefaultTopicAccess?: ReleaseAccessStatus | null
    ) => {
      setDialogState({
        open: true,
        resourceIds,
        resourceName,
        currentStatus,
        currentTopicsAccess,
        publishedTopicsAccess,
        currentDefaultTopicAccess,
      });
    },
    []
  );

  const handleUpdateAccess = useCallback(
    async (
      status: ReleaseAccessStatus,
      topicsAccess?: ReleaseAccessResourceTopic[],
      defaultTopicAccess?: ReleaseAccessStatus | null
    ) => {
      await updateArticleAccess.mutateAsync({
        articles: dialogState.resourceIds.map((id) => ({
          id,
          status,
          topicsAccess,
          defaultTopicAccess,
        })),
      });
      setDialogState((prev) => ({ ...prev, open: false }));
      setSelectedIds(new Set());
    },
    [dialogState.resourceIds, updateArticleAccess]
  );

  const handleBulkAccessClick = useCallback(() => {
    const selectedWikiArticles = rows.filter((r) => selectedIds.has(r.id));
    if (selectedWikiArticles.length === 0) return;

    handleOpenManageAccess(
      Array.from(selectedIds),
      `${selectedWikiArticles.length} wiki articles`,
      selectedWikiArticles[0].accessStatus,
      selectedWikiArticles[0].topicsAccess,
      selectedWikiArticles[0].publishedTopicsAccess,
      selectedWikiArticles[0].defaultTopicAccess
    );
  }, [rows, selectedIds, handleOpenManageAccess]);

  const getRowActions = useCallback(
    (row: WikiRow): RowActions<WikiRow> => [
      {
        label: "Manage Access",
        icon: <Pencil1Icon />,
        onClick: () =>
          handleOpenManageAccess([row.id], row.title, row.accessStatus, row.topicsAccess, row.publishedTopicsAccess, row.defaultTopicAccess),
      },
    ],
    [handleOpenManageAccess]
  );

  const handleRowClick = useCallback(
    (row: WikiRow) => {
      handleOpenManageAccess([row.id], row.title, row.accessStatus, row.topicsAccess, row.publishedTopicsAccess, row.defaultTopicAccess);
    },
    [handleOpenManageAccess]
  );

  const columns: ColumnDef<WikiRow>[] = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <Checkbox
            checked={allSelected}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.original.isSelected}
            onCheckedChange={() => handleSelectRow(row.original.id)}
            aria-label={`Select ${row.original.title}`}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        meta: {
          className: "w-[40px]",
        },
      },
      {
        header: "Wiki Name",
        accessorKey: "title",
        cell: ({ row }) => (
          <Flex align="center" gap="2">
            <Flex
              align="center"
              justify="center"
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "var(--purple-3)",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            >
              <File02Icon fill="violet" size={12} />
            </Flex>
            <Text size="2" color="gray" weight="medium" highContrast truncate>
              {row.original.title}
            </Text>
          </Flex>
        ),
      },
      {
        id: "source",
        header: "Source",
        cell: ({ row }) => {
          const source = getArticleSource(row.original.url);
          return (
            <Badge color={articleSourceColor[source] as any} size="1" variant="soft">
              {articleSourceLabel[source]}
            </Badge>
          );
        },
        meta: {
          className: "w-[120px]",
          cellTooltip: false,
        },
      },
      {
        header: "Category",
        accessorKey: "category",
        cell: ({ row }) => (
          <Badge color="gray" size="1" variant="soft">
            {row.original.category ?? "Uncategorized"}
          </Badge>
        ),
        meta: {
          className: "w-[200px]",
        },
      },
      {
        header: "Access",
        accessorKey: "accessStatus",
        cell: ({ row }) => (
          <AccessBadge
            status={row.original.accessStatus}
            isStaged={row.original.isStaged}
            topicsAccess={row.original.topicsAccess}
            publishedTopicsAccess={row.original.publishedTopicsAccess}
            topics={topics}
          />
        ),
        meta: {
          className: "w-[180px]",
          cellTooltip: false,
        },
      },
    ],
    [allSelected, handleSelectAll, handleSelectRow, topics]
  );

  return (
    <Flex direction="column" width="100%">
      <ResourcesToolbar
        title="Wiki"
        count={filteredWikiArticles.length}
        filterState={filterState}
        onFilterChange={setFilterState}
        selectedCount={selectedIds.size}
        onBulkAccessClick={handleBulkAccessClick}
        categoryFilter={{
          title: "Category",
          options: categories,
        }}
      />

      {rows.length === 0 && !isLoading && (filterState.searchQuery || filterState.accessLevels.length > 0) ? (
        <Flex align="center" justify="center" py="9">
          <Text size="2" color="gray">
            No results found
          </Text>
        </Flex>
      ) : (
        <SimpleTable
          data={rows}
          columns={columns}
          loading={isLoading}
          getRowActions={getRowActions}
          onRowClicked={handleRowClick}
        />
      )}

      <ManageAccessDialog
        open={dialogState.open}
        onOpenChange={(open) => setDialogState((prev) => ({ ...prev, open }))}
        resourceName={dialogState.resourceName}
        resourceCount={dialogState.resourceIds.length > 1 ? dialogState.resourceIds.length : undefined}
        currentStatus={dialogState.currentStatus}
        currentTopicsAccess={dialogState.currentTopicsAccess}
        publishedTopicsAccess={dialogState.publishedTopicsAccess}
        currentDefaultTopicAccess={dialogState.currentDefaultTopicAccess}
        topics={topics}
        onUpdateAccess={handleUpdateAccess}
        isLoading={updateArticleAccess.isLoading}
      />
    </Flex>
  );
}
